import React, { useContext } from "react";
import { Card, ClassificationIcon, DataTable, QueryResult, Table, ValueOrPlaceholder, } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useSearchParams } from "react-router";
import { FILE_BROWSER_ROUTES, REPORT_PAGES_ROUTES } from "../../../constants";
import { expandedRowsUpdated, focusedRowUpdated, selectExpandedRow, selectFocusedRow, } from "../../../store/malwareSlice";
import { filteredByDependency } from "../../../store/sbomSlice";
import { ExpandedRowDetails } from "../../../ui";
import { getFileName, getThreatType, isMaliciousOrSuspicious, } from "../../../utils";
import { IssuesList, LinkToReportTab, SoftwareIdentity } from "../../common";
import { ReportContext } from "../../context";
import { useReportDispatch, useReportSelector } from "../../hooks";
import { useGetThreatTypeDetailsQuery } from "../../services/malware.api";
import { generateFileBrowserLink } from "../../utils";
import DependencyReferences from "./DependencyReferences";
import FileInformation from "./FileInformation";
import styles from "./malwareDetails.module.scss";
import ScannerList from "./ScannerList";
const getScanners = (scanners) => {
    const results = { spectra: {}, other: {} };
    Object.entries(scanners).forEach(([name, result]) => {
        if (name.toLowerCase().startsWith("spectra")) {
            results.spectra[name] = result;
        }
        else {
            results.other[name] = result;
        }
    });
    return results;
};
const ThreatNameDisplay = ({ classification, }) => {
    const isTriaged = classification.classification === "Goodware";
    return (React.createElement("div", { className: styles["threat-name-container"] },
        React.createElement(ClassificationIcon, { classification: isTriaged
                ? classification.overrides[0].classification
                : classification.classification, hasGoodwareOverrides: isTriaged }),
        React.createElement("div", null,
            React.createElement("div", { className: isTriaged ? styles["triaged-result"] : undefined }, isTriaged
                ? classification.overrides[0].result
                : classification.result),
            isTriaged && (React.createElement("div", { className: styles["triaged-reason"] },
                "Triaged: ",
                classification.reason)))));
};
const MalwareDetails = ({ malware, index, }) => {
    var _a;
    const [searchParams] = useSearchParams();
    const dispatch = useReportDispatch();
    const { reportId, MalwareFindingComponent } = useContext(ReportContext);
    const expanded = useReportSelector((state) => selectExpandedRow(state, malware.threat_details.uuid));
    const focusedRow = useReportSelector(selectFocusedRow);
    const isComponent = malware.type === "COMPONENT";
    const isTriaged = malware.threat_details.classification === "Goodware";
    const scanners = getScanners(malware.threat_details.scanners);
    const { data, isFetching, isLoading, isError, error } = useGetThreatTypeDetailsQuery({
        ui_id: reportId,
        ui_item_id: (_a = getThreatType(isTriaged
            ? malware.classification.overrides[0].result
            : malware.classification.result)) !== null && _a !== void 0 ? _a : "",
    }, { skip: !expanded });
    return (React.createElement(DataTable.ExpandedRow, { id: `malware-expanded-row-${index}`, expanded: expanded, onChange: (isExpanded) => dispatch(expandedRowsUpdated({
            id: malware.threat_details.uuid,
            expand: isExpanded,
        })), focused: focusedRow === malware.threat_details.uuid, onClick: () => dispatch(focusedRowUpdated(malware.threat_details.uuid)) },
        React.createElement(DataTable.ExpandedRow.Summary, null,
            React.createElement(Table.Cell, null,
                React.createElement(ThreatNameDisplay, { classification: malware.classification })),
            React.createElement(Table.Cell, null,
                React.createElement("div", null,
                    React.createElement(LinkToReportTab, { id: `malware-file-name-link-${index}`, to: isComponent
                            ? generateFileBrowserLink({
                                searchParams,
                                componentId: malware.threat_details.uuid,
                                componentPath: malware.threat_details.path,
                                fileBrowserPage: FILE_BROWSER_ROUTES.INFO_ROUTE,
                            })
                            : REPORT_PAGES_ROUTES.SBOM_ROUTE, onClick: () => {
                            if (!isComponent) {
                                dispatch(filteredByDependency({
                                    id: malware.threat_details.uuid,
                                    name: malware.identity.product,
                                }));
                            }
                        }, color: "link", className: styles.link }, isComponent
                        ? getFileName(malware.info)
                        : `${malware.identity.product} (${malware.identity.version})`)),
                !isComponent &&
                    isMaliciousOrSuspicious(malware.threat_details.classification) && (React.createElement("div", { className: styles["threat-name-dependency-subtext"] }, `${malware.threat_details.classification} Dependency`))),
            React.createElement(Table.Cell, null,
                React.createElement(ValueOrPlaceholder, { value: malware.threat_details.reviewed ? "Yes" : undefined })),
            React.createElement(Table.Cell, null,
                React.createElement(ValueOrPlaceholder, { value: malware.threat_details.violations.length }))),
        React.createElement(DataTable.ExpandedRow.Details, null,
            React.createElement(ExpandedRowDetails, null,
                React.createElement(ExpandedRowDetails.Aside, null,
                    isComponent ? (React.createElement(FileInformation, { info: malware.info })) : (React.createElement(SoftwareIdentity, { identity: malware.identity, className: styles["software-identity"] })),
                    React.createElement(IssuesList, { violations: malware.threat_details.violations, to: REPORT_PAGES_ROUTES.ISSUES_ROUTE })),
                React.createElement(ExpandedRowDetails.Main, null,
                    React.createElement(QueryResult, { status: getQueryStatus({
                            data,
                            isLoading,
                            isFetching,
                            isError,
                            error,
                        }) }, data && (React.createElement(Card.Card, null,
                        React.createElement(Card.Title, null, data.title),
                        React.createElement(Card.Content, null, data.description)))),
                    !isTriaged && MalwareFindingComponent && (React.createElement(MalwareFindingComponent, { isAnalystVetted: malware.threat_details.reviewed })),
                    React.createElement(ScannerList, { headerText: "Spectra detections", scanners: scanners.spectra }),
                    React.createElement(ScannerList, { headerText: "Third-party detections", scanners: scanners.other, firstScanned: malware.threat_details.cloud_fs }),
                    !isComponent && (React.createElement(DependencyReferences, { rowIndex: index, dependency: malware })))))));
};
export default MalwareDetails;
//# sourceMappingURL=MalwareDetails.js.map